import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { RewardsCoordinatorContext } from '../RewardsCoordinatorContext';
import { RedeemController, type RedeemPhase } from '../RedeemController';
import type { SkuDetail, RedeemResult } from '../../../services/rewards/types';

export interface RedeemFlowState {
  phase: RedeemPhase;
  skuDetail: SkuDetail | null;
  redeemResult: RedeemResult | null;
  error: string | null;
  loading: boolean;
  reserving: boolean;
  secondsLeft: number;
  startReserve: () => void;
  confirmRedemption: () => void;
  cancel: () => void;
  retryReserve: () => void;
  retry: () => void;
  retryFetchDetail: () => void;
}

export function useRedeemFlow(skuId: string): RedeemFlowState {
  const coordinator = useContext(RewardsCoordinatorContext);
  if (!coordinator) {
    throw new Error('useRedeemFlow must be used within a RewardsCoordinatorProvider');
  }

  const controllerRef = useRef<RedeemController | null>(null);
  const [, forceRender] = useState(0);

  useEffect(() => {
    const controller = coordinator.createRedeemFlow(skuId);
    controllerRef.current = controller;

    const unsub = controller.subscribe(() => {
      forceRender((n) => n + 1);
    });

    controller.start();

    return () => {
      unsub();
      controller.destroy();
      controllerRef.current = null;
    };
  }, [coordinator, skuId]);

  const ctrl = controllerRef.current;

  const startReserve = useCallback(() => { ctrl?.startReserve(); }, [ctrl]);
  const confirmRedemption = useCallback(() => { ctrl?.confirmRedemption(); }, [ctrl]);
  const cancel = useCallback(() => { ctrl?.cancel(); }, [ctrl]);
  const retryReserve = useCallback(() => { ctrl?.retryReserve(); }, [ctrl]);
  const retry = useCallback(() => { ctrl?.retryAfterNetworkError(); }, [ctrl]);
  const retryFetchDetail = useCallback(() => { ctrl?.retryFetchDetail(); }, [ctrl]);

  return {
    phase: ctrl?.getPhase() ?? 'detail',
    skuDetail: ctrl?.getSkuDetail() ?? null,
    redeemResult: ctrl?.getRedeemResult() ?? null,
    error: ctrl?.getError() ?? null,
    loading: ctrl?.isLoading() ?? true,
    reserving: ctrl?.isReserving() ?? false,
    secondsLeft: ctrl?.getSecondsLeft() ?? 30,
    startReserve,
    confirmRedemption,
    cancel,
    retryReserve,
    retry,
    retryFetchDetail,
  };
}
